/**
 * Posts service
 * Provides typed functions for interacting with the posts_with_author view
 */

import { supabase } from '../supabase';
import type { PostWithAuthor } from '../types/database';

const PAGE_SIZE = 20;

/**
 * Paginated response type for infinite queries
 */
export type PaginatedPostsResponse = {
  data: PostWithAuthor[];
  nextCursor: number | undefined;
};

/**
 * Fetch all posts with author information and comment counts
 * Uses the posts_with_author view for efficient single-query fetching
 * Orders by created_at descending (newest first)
 */
export async function getPosts(): Promise<PostWithAuthor[]> {
  const { data, error } = await supabase
    .from('posts_with_author')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch posts: ${error.message}`);
  }

  return (data as PostWithAuthor[]) || [];
}

/**
 * Fetch posts with pagination
 */
export async function getPostsPaginated(
  pageParam: number = 0
): Promise<PaginatedPostsResponse> {
  const { data, error } = await supabase
    .from('posts_with_author')
    .select('*')
    .order('created_at', { ascending: false })
    .range(pageParam, pageParam + PAGE_SIZE - 1);

  if (error) {
    throw new Error(`Failed to fetch posts: ${error.message}`);
  }

  const posts = (data as PostWithAuthor[]) || [];

  return {
    data: posts,
    nextCursor: posts.length === PAGE_SIZE ? pageParam + PAGE_SIZE : undefined,
  };
}

/**
 * Fetch a single post by ID with author information and comment count
 */
export async function getPostById(id: number): Promise<PostWithAuthor> {
  const { data, error } = await supabase
    .from('posts_with_author')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Failed to fetch post: ${error.message}`);
  }

  return data as PostWithAuthor;
}

/**
 * Create a new post
 * Inserts into the posts table and returns the created post with author information
 */
export async function createPost(
  userId: string,
  content: string,
  imageUrl?: string | null
): Promise<PostWithAuthor> {
  // Insert into posts table
  const { data: insertedPost, error: insertError } = await supabase
    .from('posts')
    .insert({
      user_id: userId,
      content: content,
      image_url: imageUrl || null,
    })
    .select()
    .single();

  if (insertError) {
    throw new Error(`Failed to create post: ${insertError.message}`);
  }

  // Fetch the created post from the view to get author information
  const { data: postWithAuthor, error: fetchError } = await supabase
    .from('posts_with_author')
    .select('*')
    .eq('id', insertedPost.id)
    .single();

  if (fetchError) {
    throw new Error(`Failed to fetch created post: ${fetchError.message}`);
  }

  return postWithAuthor as PostWithAuthor;
}

/**
 * Update an existing post
 * Updates the content in the posts table and returns the updated post with author information
 */
export async function updatePost(
  postId: number,
  content: string
): Promise<PostWithAuthor> {
  // Update the post in the posts table
  const { error: updateError } = await supabase
    .from('posts')
    .update({
      content: content,
      updated_at: new Date().toISOString(),
    })
    .eq('id', postId);

  if (updateError) {
    throw new Error(`Failed to update post: ${updateError.message}`);
  }

  // Fetch the updated post from the view to get author information
  const { data: postWithAuthor, error: fetchError } = await supabase
    .from('posts_with_author')
    .select('*')
    .eq('id', postId)
    .single();

  if (fetchError) {
    throw new Error(`Failed to fetch updated post: ${fetchError.message}`);
  }

  return postWithAuthor as PostWithAuthor;
}

/**
 * Delete a post
 * Deletes the post from the posts table (cascades to comments via foreign key)
 */
export async function deletePost(postId: number): Promise<void> {
  const { error } = await supabase.from('posts').delete().eq('id', postId);

  if (error) {
    throw new Error(`Failed to delete post: ${error.message}`);
  }
}

/**
 * Get pending posts for moderation queue (admin only)
 */
export async function getPendingPosts(): Promise<PostWithAuthor[]> {
  const { data, error } = await supabase
    .from('posts_with_author')
    .select('*')
    .eq('moderation_status', 'pending')
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to fetch pending posts: ${error.message}`);
  }

  return (data as PostWithAuthor[]) || [];
}

/**
 * Get the number of pending posts for admin notification UI
 */
export async function getPendingPostsCount(): Promise<number> {
  const { count, error } = await supabase
    .from('posts')
    .select('id', { count: 'exact', head: true })
    .eq('moderation_status', 'pending');

  if (error) {
    throw new Error(`Failed to fetch pending posts count: ${error.message}`);
  }

  return count || 0;
}

/**
 * Approve a post (admin only)
 */
export async function approvePost(
  postId: number,
  moderatorId: string
): Promise<PostWithAuthor> {
  const { error: updateError } = await supabase
    .from('posts')
    .update({
      moderation_status: 'approved',
      moderated_by: moderatorId,
      moderated_at: new Date().toISOString(),
      rejection_reason: null,
    })
    .eq('id', postId);

  if (updateError) {
    throw new Error(`Failed to approve post: ${updateError.message}`);
  }

  // Fetch the updated post from the view
  const { data: postWithAuthor, error: fetchError } = await supabase
    .from('posts_with_author')
    .select('*')
    .eq('id', postId)
    .single();

  if (fetchError) {
    throw new Error(`Failed to fetch approved post: ${fetchError.message}`);
  }

  return postWithAuthor as PostWithAuthor;
}

/**
 * Reject a post with optional reason (admin only)
 */
export async function rejectPost(
  postId: number,
  moderatorId: string,
  reason?: string
): Promise<PostWithAuthor> {
  const { error: updateError } = await supabase
    .from('posts')
    .update({
      moderation_status: 'rejected',
      moderated_by: moderatorId,
      moderated_at: new Date().toISOString(),
      rejection_reason: reason || null,
    })
    .eq('id', postId);

  if (updateError) {
    throw new Error(`Failed to reject post: ${updateError.message}`);
  }

  // Fetch the updated post from the view
  const { data: postWithAuthor, error: fetchError } = await supabase
    .from('posts_with_author')
    .select('*')
    .eq('id', postId)
    .single();

  if (fetchError) {
    throw new Error(`Failed to fetch rejected post: ${fetchError.message}`);
  }

  return postWithAuthor as PostWithAuthor;
}

/**
 * Call edge function to notify admins of new pending post
 */
export async function notifyAdminsOfPendingPost(postId: number): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('User must be authenticated to send notifications');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL is not configured');
  }

  try {
    const response = await fetch(
      `${supabaseUrl}/functions/v1/notify-post-pending`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ postId }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      console.error('Failed to notify admins:', data);
      // Don't throw - notification failure shouldn't break post creation
    }
  } catch (error) {
    console.error('Error calling notify-post-pending:', error);
    // Don't throw - notification failure shouldn't break post creation
  }
}

/**
 * Call edge function to notify user of moderation result
 */
export async function notifyUserOfModerationResult(
  postId: number,
  status: 'approved' | 'rejected',
  reason?: string
): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('User must be authenticated to send notifications');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL is not configured');
  }

  try {
    const response = await fetch(
      `${supabaseUrl}/functions/v1/notify-post-moderated`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ postId, status, reason }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      console.error('Failed to notify user:', data);
      // Don't throw - notification failure shouldn't break moderation
    }
  } catch (error) {
    console.error('Error calling notify-post-moderated:', error);
    // Don't throw - notification failure shouldn't break moderation
  }
}
